import { formatTwoDecimals, roundToTwoDecimals, sumRounded } from './decimalUtils';
import type { components } from '../Types/api';

type Schemas = components['schemas'];
type PackageSimulationDto = Schemas['Cockpit.Models.PriceSimulation.PackageSimulationDto'];
type PriceSimulationPointDto = Schemas['Cockpit.Models.PriceSimulation.PriceSimulationPointDto'];
type TieredPricePointDto = Schemas['Cockpit.Models.PriceSimulation.TieredPricePointDto'];

const roundOptional = <T extends number | null | undefined>(value: T): T => (typeof value === 'number' ? (roundToTwoDecimals(value) as T) : value);

const isUnchanged = (current: number | null | undefined, next: number | null | undefined): boolean => typeof current === 'number' && current === next;

const roundTieredPrice = (tp: TieredPricePointDto): TieredPricePointDto => {
    const currentUnitPrice = roundOptional(tp.currentUnitPrice);
    const newUnitPrice = roundOptional(tp.newUnitPrice);
    return { ...tp, currentUnitPrice, newUnitPrice, adjustmentPercentage: isUnchanged(currentUnitPrice, newUnitPrice) ? 0 : tp.adjustmentPercentage };
};

const roundPricePoint = (point: PriceSimulationPointDto): PriceSimulationPointDto => {
    const currentPrice = roundOptional(point.currentPrice);
    const newPrice = roundOptional(point.newPrice);
    return {
        ...point,
        currentPrice,
        newPrice,
        adjustmentPercentage: isUnchanged(currentPrice, newPrice) ? 0 : point.adjustmentPercentage,
        tieredPrices: point.tieredPrices?.map(roundTieredPrice) ?? point.tieredPrices,
    };
};

export const planLengthInMonths = (planIdentifier: string | null | undefined): number | null => {
    const months = planIdentifier?.match(/\((\d+)M\)/i) ?? planIdentifier?.match(/^(\d+)M$/i);
    return months ? parseInt(months[1], 10) : null;
};

export const sumAvailablePrices = (prices: (number | null)[]): number | null => {
    const available = prices.filter((price): price is number => price !== null);
    return available.length > 0 ? sumRounded(available) : null;
};

export const formatPriceOrDash = (price: number | null): string => (price === null ? '–' : `${formatTwoDecimals(price)} SEK`);

export const roundPackagePrices = (pkg: PackageSimulationDto): PackageSimulationDto => ({
    ...pkg,
    tiers: pkg.tiers?.map((tier) => ({
        ...tier,
        productGroups: tier.productGroups?.map((group) => ({
            ...group,
            products: group.products?.map((product) => ({
                ...product,
                prices: product.prices ? Object.fromEntries(Object.entries(product.prices).map(([plan, point]) => [plan, roundPricePoint(point)])) : product.prices,
            })),
        })),
    })),
});
